import { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { AudienceFields, type AudienceForm } from '../../../../../types.ts';
import {
  applyMarketForcedUnionOperators,
  groupsPathForList,
  parseCriteriaListFieldNameFromFieldPath,
} from '../../../../../utils/applyMarketForcedUnionOperators.ts';
import {
  hasInternationalCriteriaInDefinition,
  hasInternationalCriteriaInGroups,
} from '../../../../../utils/hasInternationalCriteria.ts';
import { getShouldUseRegionFirstFlow } from '../../../../../utils/marketOperatorRestrictions.ts';

export const useEnforceMarketUnionAfterApply = ({
  isCreate,
  isCreateFromTemplate,
}: {
  isCreate?: boolean;
  isCreateFromTemplate?: boolean;
}) => {
  const { control, getValues, setValue } = useFormContext<AudienceForm>();
  const observableAccountId = useWatch({ name: AudienceFields.OrganizationId, control }) ?? '';

  return useCallback(
    (fieldPath: string): boolean => {
      const fieldName = parseCriteriaListFieldNameFromFieldPath(fieldPath);

      if (!fieldName) {
        return false;
      }

      const def = getValues(AudienceFields.NormalizedDefinition);
      const groupsPath = groupsPathForList(fieldName);
      const listGroups = getValues(groupsPath);
      const hasInternational =
        hasInternationalCriteriaInDefinition(def) || hasInternationalCriteriaInGroups(listGroups);

      const shouldUseRegionFirstFlow = getShouldUseRegionFirstFlow({
        isCreate,
        isCreateFromTemplate,
        hasInternationalCriteria: hasInternational,
      });

      return applyMarketForcedUnionOperators({
        fieldName,
        getValues,
        setValue,
        shouldUseRegionFirstFlow,
        accountId: observableAccountId,
        includeSellableMultipartner: true,
      });
    },
    [observableAccountId, getValues, isCreate, isCreateFromTemplate, setValue]
  );
};
